import { CODES, KEYS, getSizeFromPoints } from "@excalidraw/common";

import {
  CaptureUpdateAction,
  LinearElementEditor,
  addNodeOnSegment,
  isPenPath,
  joinEndpoints,
  makeCorner,
  makeCurve,
  mergeNodes,
  newLinearElement,
  separateAtNodes,
} from "@excalidraw/element";

import { pointFrom } from "@excalidraw/math";

import type { LocalPoint } from "@excalidraw/math";

import type {
  PenPathData,
  PenPathElement,
  PenPathUpdate,
} from "@excalidraw/element";

import { DEFAULT_CATEGORIES } from "../components/CommandPalette/CommandPalette";

import { register } from "./register";

import type { AppClassProperties, AppState } from "../types";

/**
 * Non-null only while actively editing a pen path (Task 6/7 line + handles):
 * `selectedLinearElement.isEditing` and the edited element `isPenPath`.
 * Shared by every action below (predicate/keyTest) and by App.tsx's keydown
 * handler (tool-switch suppression + arrow-key node nudge).
 */
export const getEditedPenPath = (
  appState: AppState,
  app: AppClassProperties,
): { element: PenPathElement; indices: number[] } | null => {
  const selected = appState.selectedLinearElement;
  if (!selected?.isEditing) {
    return null;
  }
  const element = LinearElementEditor.getElement<PenPathElement>(
    selected.elementId,
    app.scene.getNonDeletedElementsMap(),
  );
  if (!element || !isPenPath(element)) {
    return null;
  }
  return {
    element,
    indices: [...(selected.selectedPointsIndices ?? [])],
  };
};

/**
 * True while actively editing a pen path — used to gate other actions'
 * `keyTest`s whose shortcuts collide with pen-path shortcuts (ActionManager
 * fires no action when more than one `keyTest` matches a keystroke).
 */
export const isEditingPenPath = (
  appState: AppState,
  app: AppClassProperties,
): boolean => getEditedPenPath(appState, app) != null;

const toPenPathData = (element: PenPathElement): PenPathData => ({
  points: element.points,
  handles: element.handles,
  polygon: element.polygon,
});

/**
 * Writes a nodeOps result back onto the element: normalizes points (start
 * point pinned at [0,0], per `LinearElementEditor.getNormalizeElementPointsAndCoords`)
 * and mutates through the scene so the change is undoable by the caller's
 * `captureUpdate`.
 */
export const applyPenUpdate = (
  element: PenPathElement,
  app: AppClassProperties,
  update: PenPathUpdate,
) => {
  const normalized = LinearElementEditor.getNormalizeElementPointsAndCoords({
    ...element,
    ...update,
  });
  app.scene.mutateElement(element, {
    ...update,
    points: normalized.points,
    x: normalized.x,
    y: normalized.y,
    ...getSizeFromPoints(normalized.points),
  });
};

const isContiguous = (indices: number[]): boolean => {
  if (indices.length < 2) {
    return false;
  }
  const sorted = [...indices].sort((a, b) => a - b);
  return sorted.every((v, i) => i === 0 || v === sorted[i - 1] + 1);
};

const isSegmentSelection = (indices: number[]): boolean =>
  indices.length === 2 && Math.abs(indices[0] - indices[1]) === 1;

const hasCurve = (element: PenPathElement, indices: number[]): boolean =>
  indices.length > 0 && indices.some((i) => element.handles[i] != null);

const allCorners = (element: PenPathElement, indices: number[]): boolean =>
  indices.length > 0 && indices.every((i) => element.handles[i] == null);

// plain-letter shortcuts below explicitly exclude ctrl/cmd + alt so they
// don't also fire alongside modifier-based shortcuts on the same letter
// (e.g. K also being "insert link" under Ctrl/Cmd) — ActionManager cancels
// a keypress outright when more than one action's keyTest matches it.
const isPlainKey = (event: React.KeyboardEvent | KeyboardEvent, key: string) =>
  !event[KEYS.CTRL_OR_CMD] && !event.altKey && event.key.toLowerCase() === key;

export const penSetMoveMode = register({
  name: "penSetMoveMode",
  label: "labels.penPath.moveNodes",
  category: DEFAULT_CATEGORIES.elements,
  trackEvent: { category: "element" },
  predicate: (elements, appState, _, app) =>
    getEditedPenPath(appState, app) !== null,
  perform: (elements, appState) => ({
    appState: { ...appState, penEditMode: "move" },
    captureUpdate: CaptureUpdateAction.NEVER,
  }),
  keyTest: (event, appState, elements, app) =>
    getEditedPenPath(appState, app) !== null && isPlainKey(event, "m"),
});

export const penSetDrawMode = register({
  name: "penSetDrawMode",
  label: "labels.penPath.drawNodes",
  category: DEFAULT_CATEGORIES.elements,
  trackEvent: { category: "element" },
  predicate: (elements, appState, _, app) =>
    getEditedPenPath(appState, app) !== null,
  perform: (elements, appState) => ({
    appState: { ...appState, penEditMode: "draw" },
    captureUpdate: CaptureUpdateAction.NEVER,
  }),
  keyTest: (event, appState, elements, app) =>
    getEditedPenPath(appState, app) !== null && isPlainKey(event, "p"),
});

export const penMakeCorner = register({
  name: "penMakeCorner",
  label: "labels.penPath.makeCorner",
  category: DEFAULT_CATEGORIES.elements,
  trackEvent: { category: "element" },
  predicate: (elements, appState, _, app) => {
    const edited = getEditedPenPath(appState, app);
    return !!edited && hasCurve(edited.element, edited.indices);
  },
  perform: (elements, appState, _, app) => {
    const edited = getEditedPenPath(appState, app);
    if (!edited || !hasCurve(edited.element, edited.indices)) {
      return false;
    }
    applyPenUpdate(
      edited.element,
      app,
      makeCorner(toPenPathData(edited.element), edited.indices),
    );
    return {
      elements,
      appState,
      captureUpdate: CaptureUpdateAction.IMMEDIATELY,
    };
  },
  keyTest: (event, appState, elements, app) =>
    getEditedPenPath(appState, app) !== null && isPlainKey(event, "x"),
});

export const penMakeCurve = register({
  name: "penMakeCurve",
  label: "labels.penPath.makeCurve",
  category: DEFAULT_CATEGORIES.elements,
  trackEvent: { category: "element" },
  predicate: (elements, appState, _, app) => {
    const edited = getEditedPenPath(appState, app);
    return !!edited && allCorners(edited.element, edited.indices);
  },
  perform: (elements, appState, _, app) => {
    const edited = getEditedPenPath(appState, app);
    if (!edited || !allCorners(edited.element, edited.indices)) {
      return false;
    }
    applyPenUpdate(
      edited.element,
      app,
      makeCurve(toPenPathData(edited.element), edited.indices),
    );
    return {
      elements,
      appState,
      captureUpdate: CaptureUpdateAction.IMMEDIATELY,
    };
  },
  keyTest: (event, appState, elements, app) =>
    getEditedPenPath(appState, app) !== null && isPlainKey(event, "c"),
});

export const penAddNode = register({
  name: "penAddNode",
  label: "labels.penPath.addNode",
  category: DEFAULT_CATEGORIES.elements,
  trackEvent: { category: "element" },
  predicate: (elements, appState, _, app) => {
    const edited = getEditedPenPath(appState, app);
    return !!edited && isSegmentSelection(edited.indices);
  },
  perform: (elements, appState, _, app) => {
    const edited = getEditedPenPath(appState, app);
    if (!edited || !isSegmentSelection(edited.indices)) {
      return false;
    }
    const { element, indices } = edited;
    const segmentIndex = Math.min(indices[0], indices[1]);
    applyPenUpdate(
      element,
      app,
      addNodeOnSegment(toPenPathData(element), segmentIndex),
    );
    return {
      elements,
      appState: {
        ...appState,
        selectedLinearElement: {
          ...appState.selectedLinearElement!,
          selectedPointsIndices: [segmentIndex + 1],
        },
      },
      captureUpdate: CaptureUpdateAction.IMMEDIATELY,
    };
  },
  keyTest: (event, appState, elements, app) =>
    getEditedPenPath(appState, app) !== null &&
    event.shiftKey &&
    event.key === "+",
});

export const penMergeNodes = register({
  name: "penMergeNodes",
  label: "labels.penPath.mergeNodes",
  category: DEFAULT_CATEGORIES.elements,
  trackEvent: { category: "element" },
  predicate: (elements, appState, _, app) => {
    const edited = getEditedPenPath(appState, app);
    return !!edited && isContiguous(edited.indices);
  },
  perform: (elements, appState, _, app) => {
    const edited = getEditedPenPath(appState, app);
    if (!edited) {
      return false;
    }
    const update = mergeNodes(toPenPathData(edited.element), edited.indices);
    if (!update) {
      return false;
    }
    applyPenUpdate(edited.element, app, update);
    const sorted = [...edited.indices].sort((a, b) => a - b);
    return {
      elements,
      appState: {
        ...appState,
        selectedLinearElement: {
          ...appState.selectedLinearElement!,
          selectedPointsIndices: [sorted[0]],
        },
      },
      captureUpdate: CaptureUpdateAction.IMMEDIATELY,
    };
  },
  keyTest: (event, appState, elements, app) =>
    getEditedPenPath(appState, app) !== null &&
    event[KEYS.CTRL_OR_CMD] &&
    event.key.toLowerCase() === "j",
});

export const penJoinNodes = register({
  name: "penJoinNodes",
  label: "labels.penPath.joinNodes",
  category: DEFAULT_CATEGORIES.elements,
  trackEvent: { category: "element" },
  predicate: (elements, appState, _, app) => {
    const edited = getEditedPenPath(appState, app);
    return !!edited && joinEndpoints(toPenPathData(edited.element)) !== null;
  },
  perform: (elements, appState, _, app) => {
    const edited = getEditedPenPath(appState, app);
    if (!edited) {
      return false;
    }
    const update = joinEndpoints(toPenPathData(edited.element));
    if (!update) {
      return false;
    }
    applyPenUpdate(edited.element, app, update);
    return {
      elements,
      appState,
      captureUpdate: CaptureUpdateAction.IMMEDIATELY,
    };
  },
  keyTest: (event, appState, elements, app) =>
    getEditedPenPath(appState, app) !== null && isPlainKey(event, "j"),
});

export const penSeparateNodes = register({
  name: "penSeparateNodes",
  label: "labels.penPath.separateNodes",
  category: DEFAULT_CATEGORIES.elements,
  trackEvent: { category: "element" },
  predicate: (elements, appState, _, app) => {
    const edited = getEditedPenPath(appState, app);
    return (
      !!edited &&
      separateAtNodes(toPenPathData(edited.element), edited.indices) !== null
    );
  },
  perform: (elements, appState, _, app) => {
    const edited = getEditedPenPath(appState, app);
    if (!edited) {
      return false;
    }
    const { element, indices } = edited;
    const result = separateAtNodes(toPenPathData(element), indices);
    if (!result) {
      return false;
    }

    applyPenUpdate(element, app, result.first);

    const nextSelectedLinearElement = {
      ...appState.selectedLinearElement!,
      selectedPointsIndices: null,
    };

    if (!result.second) {
      // closed path opened at the node: single resulting element
      return {
        elements,
        appState: {
          ...appState,
          selectedLinearElement: nextSelectedLinearElement,
        },
        captureUpdate: CaptureUpdateAction.IMMEDIATELY,
      };
    }

    // open path split in two: `second` isn't normalized (its points don't
    // start at [0,0]) — offset it into its own local space, same as
    // `applyPenUpdate` does for `first` via getNormalizeElementPointsAndCoords.
    const firstPointOffset = result.second.points[0];
    const secondPoints = result.second.points.map((p) =>
      pointFrom<LocalPoint>(
        p[0] - firstPointOffset[0],
        p[1] - firstPointOffset[1],
      ),
    );
    const secondElement = newLinearElement({
      type: "line",
      x: element.x + firstPointOffset[0],
      y: element.y + firstPointOffset[1],
      angle: element.angle,
      points: secondPoints,
      ...getSizeFromPoints(secondPoints),
      handles: result.second.handles,
      polygon: false,
      strokeColor: element.strokeColor,
      backgroundColor: element.backgroundColor,
      fillStyle: element.fillStyle,
      strokeWidth: element.strokeWidth,
      strokeStyle: element.strokeStyle,
      roughness: element.roughness,
      opacity: element.opacity,
      roundness: element.roundness,
      locked: element.locked,
      groupIds: element.groupIds,
      frameId: element.frameId,
    }) as unknown as PenPathElement;

    return {
      elements: [...elements, secondElement],
      appState: {
        ...appState,
        selectedLinearElement: nextSelectedLinearElement,
      },
      captureUpdate: CaptureUpdateAction.IMMEDIATELY,
    };
  },
  keyTest: (event, appState, elements, app) =>
    getEditedPenPath(appState, app) !== null && isPlainKey(event, "k"),
});

export const penToggleSnap = register({
  name: "penToggleSnap",
  label: "labels.penPath.snapToNodes",
  category: DEFAULT_CATEGORIES.elements,
  trackEvent: { category: "element" },
  predicate: () => true,
  checked: (appState) => appState.penSnapEnabled,
  perform: (elements, appState) => ({
    appState: { ...appState, penSnapEnabled: !appState.penSnapEnabled },
    captureUpdate: CaptureUpdateAction.NEVER,
  }),
  keyTest: (event, appState, elements, app) =>
    getEditedPenPath(appState, app) !== null &&
    event[KEYS.CTRL_OR_CMD] &&
    event.code === CODES.QUOTE,
});
